import { CheckboxInput, Typography } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import { configSelector } from "@/redux/slices/config/selectors";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { clsx } from "@/utils";

// App-level fallbacks when the resolved operator config carries no terms/privacy urls.
const DEFAULT_TERMS_URL = "https://rideminder.com/terms";
const DEFAULT_PRIVACY_URL = "https://rideminder.com/privacy";

interface GuestTermsConsentProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}

/**
 * Consent checkbox shown above Book Trip on the guest summary (Figma 5546-151657). CHECKED by default
 * — the guest opts out rather than in — and gates submission via the caller's `canBook`. The Terms /
 * Privacy Policy links use the resolved operator config's urls when present, else the app defaults.
 * The visible box is the CheckboxInput atom (labelled for screen readers); the rich consent copy with
 * links sits beside it so the anchors stay individually clickable.
 */
export const GuestTermsConsent = ({ checked, onChange, className }: GuestTermsConsentProps) => {
  // Prefer the booking operator's (provider's) own terms/privacy — the guest is booking with THEM
  // (resolved from the quote), which may differ from the domain default in `config`. Fall back to the
  // domain config, then the app defaults.
  const operator = useAppSelector(activeOperatorSelector);
  const config = useAppSelector(configSelector);
  const termsUrl = operator?.urls?.terms || config?.urls?.terms || DEFAULT_TERMS_URL;
  const privacyUrl = operator?.urls?.privacy || config?.urls?.privacy || DEFAULT_PRIVACY_URL;

  return (
    <div className={clsx("flex items-start gap-2", className)}>
      {/* shrink-0 wrapper aligns the box to the first line; layout classes go here, NOT on CheckboxInput
          (the atom re-applies its className to the inner <input>, which distorts spacing). */}
      <span className="mt-px shrink-0">
        <CheckboxInput
          id="guest-terms-agree"
          label=""
          variant="check"
          size="md"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-label="I agree to RideMinder creating an account for me using these details"
        />
      </span>
      <Typography variant="small" className="block leading-snug text-neutral-dark-gray">
        I agree to RideMinder creating an account for me using these details, so I can manage my bookings and track my driver. See our{" "}
        <a href={termsUrl} target="_blank" rel="noreferrer" className="underline">
          Terms
        </a>{" "}
        and{" "}
        <a href={privacyUrl} target="_blank" rel="noreferrer" className="underline">
          Privacy Policy
        </a>
        .
      </Typography>
    </div>
  );
};
